import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Link2,
  Loader2,
  MessageSquare,
  Plus,
  Search,
  Send,
  X,
  XCircle,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import {
  useAddInviterCreatorRequestComment,
  useAttachInviteToRequest,
  useDetachInviteFromRequest,
  useInviterCreatorRequest,
  useUpdateInviterCreatorRequest,
} from '@/hooks/useInviterCreatorRequests';
import { useApi } from '@/hooks/useApi';
import type {
  CreatorRequestCancellationReason,
  CreatorRequestStatus,
  OutreachInvite,
} from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pill, toneFor } from '@/components/ui/pill';
import { Tag, channelToneFor, channelLabel } from '@/components/ui/tag';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMe } from '@/hooks/useMe';
import { firstNameOf } from '@/lib/displayName';
import { cn } from '@/lib/utils';
import {
  cancellationReasonLabel,
  creatorTypeLabel,
  inviteStatusLabel,
  requestStatusLabel,
} from '@/lib/labels';
import { channelLabel as channelName } from '@/lib/outreach-copy';
import { InviteStagePill } from '@/components/invites/InviteStagePill';
import {
  REQUEST_TYPE_LABELS,
  commentAuthorLabel,
  useNicheNames,
  useTeamName,
} from '@/pages/CreatorRequestBatches';

/**
 * The five funnel statuses in order. COMPLETED and CANCELLED are terminal
 * outcomes, not steps in the funnel; they get separate UI treatment. Typed
 * `as const` so STEP_TONE below can be a strict Record over just these five.
 * Labels come from `requestStatusLabel` so they match every other surface.
 */
const FUNNEL = (
  ['REQUESTED', 'IN_PROGRESS', 'AWAITING_RESPONSE', 'AWAITING_RATE', 'ROSTERED'] as const
).map((value) => ({ value, label: requestStatusLabel(value) })) satisfies readonly {
  value: CreatorRequestStatus;
  label: string;
}[];

const CANCELLATION_REASONS: CreatorRequestCancellationReason[] = [
  'GHOSTED',
  'TOO_EXPENSIVE',
  'REFUSING_PROMOS',
  'OUT_OF_NICHE',
  'OTHER',
];

export function InviterCreatorRequestDetailPage(): JSX.Element {
  const { publicId } = useParams<{ publicId: string }>();
  const query = useInviterCreatorRequest(publicId ?? null);
  const update = useUpdateInviterCreatorRequest();
  const addComment = useAddInviterCreatorRequestComment();
  const attachInvite = useAttachInviteToRequest();
  const detachInvite = useDetachInviteFromRequest();
  const api = useApi();

  const [commentBody, setCommentBody] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState<
    CreatorRequestCancellationReason | ''
  >('');
  const [cancellationNote, setCancellationNote] = useState('');
  const [attachSearch, setAttachSearch] = useState('');
  const [showAttachPanel, setShowAttachPanel] = useState(false);

  const request = query.data ?? null;

  // Backfill picker — pre-populates with the inviter's 10 most recent invites
  // so the dropdown isn't empty until they type. Search narrows the same list.
  const attachable = useQuery({
    queryKey: ['inviter', 'invites', 'backfill', publicId, attachSearch],
    enabled: !!publicId && showAttachPanel,
    queryFn: () =>
      api.getPaged<OutreachInvite[]>('/portal/invites', {
        notAttachedTo: publicId ?? '',
        ...(attachSearch.trim() ? { search: attachSearch.trim() } : {}),
        limit: 10,
      }),
  });

  const attachedInvites = useMemo(
    () => request?.attachedInvites ?? [],
    [request?.attachedInvites],
  );

  const me = useMe();
  const teamTracks =
    me.data?.role === 'outreacher' ? me.data.teamSettings.clippingResolveRequired : undefined;
  const teamName = useTeamName();
  const nicheName = useNicheNames();

  async function moveTo(next: CreatorRequestStatus): Promise<void> {
    if (!request) return;
    if (next === request.status) return;
    try {
      await update.mutateAsync({
        publicId: request.publicId,
        body: { status: next },
      });
      const label = requestStatusLabel(next);
      toast.success(`Moved to ${label}`);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function markComplete(): Promise<void> {
    if (!request) return;
    try {
      await update.mutateAsync({
        publicId: request.publicId,
        body: { status: 'COMPLETED' },
      });
      toast.success('Marked completed');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function confirmCancel(): Promise<void> {
    if (!request) return;
    if (!cancellationReason) {
      toast.error('Pick a reason first');
      return;
    }
    try {
      await update.mutateAsync({
        publicId: request.publicId,
        body: {
          status: 'CANCELLED',
          cancellationReason: cancellationReason || null,
          cancellationNote: cancellationNote.trim() || null,
        },
      });
      toast.success('Request cancelled');
      setCancelOpen(false);
      setCancellationReason('');
      setCancellationNote('');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function postComment(): Promise<void> {
    if (!request || !commentBody.trim()) return;
    try {
      await addComment.mutateAsync({
        publicId: request.publicId,
        body: commentBody.trim(),
      });
      setCommentBody('');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  if (query.isLoading) {
    return (
      <div className="flex items-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
      </div>
    );
  }
  if (query.error || !request) {
    return (
      <p className="text-sm text-destructive">
        {(query.error as Error)?.message ?? 'Not found'}
      </p>
    );
  }

  const isCompleted = request.status === 'COMPLETED';
  const isCancelled = request.status === 'CANCELLED';
  const isRostered = request.status === 'ROSTERED';
  // ROSTERED is outreach-owned and reversible — an outreacher who clicked it
  // by accident should be able to backtrack from the stepper. Only CM-closed
  // states (COMPLETED, CANCELLED) hide the flow + comment input behind a
  // banner.
  const isClosedByCM = isCompleted || isCancelled;

  return (
    <div className="space-y-5">
      <Link
        to="/portal/creator-requests"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" /> Requests
      </Link>

      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            {request.requestType === 'CREATOR'
              ? request.targetIdentifier
              : request.niches.length > 0
                ? request.niches.map(nicheName).join(' + ')
                : 'Niche request'}
          </h1>
          <Pill tone={toneFor(request.status)}>{requestStatusLabel(request.status)}</Pill>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Tag tone={request.requestType === 'NICHE' ? 'niche' : 'creator'}>
            {REQUEST_TYPE_LABELS[request.requestType]}
          </Tag>
          <Tag>{teamName(request.targetTeam)}</Tag>
          {request.channel && (
            <Tag tone={channelToneFor(request.channel)}>
              {channelLabel(request.channel)}
            </Tag>
          )}
          <span className="text-muted-foreground/60">·</span>
          <span>
            <span className="text-foreground/80">
              {firstNameOf(request.requesterName) || request.requesterName}
            </span>
            <span className="text-muted-foreground"> · {request.requesterEmail}</span>
          </span>
          <span className="text-muted-foreground/60">·</span>
          <span>{formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}</span>
          <span className="text-muted-foreground/60">·</span>
          <span className="font-mono text-[11px]">{request.publicId}</span>
        </div>
      </header>

      {isCompleted && (
        <ClosedBanner
          tone="success"
          title="Completed"
          body="This request is closed. Nothing more to do on it."
        />
      )}
      {isCancelled && (
        <ClosedBanner
          tone="destructive"
          title={`Cancelled: ${request.cancellationReason ? cancellationReasonLabel(request.cancellationReason) : 'no reason given'}`}
          body={request.cancellationNote ?? null}
        />
      )}

      {!isClosedByCM && (
        <StatusFlow
          current={request.status}
          onMove={(s) => moveTo(s)}
          onComplete={markComplete}
          onCancelStart={() => setCancelOpen((v) => !v)}
          disabled={update.isPending}
          cancelOpen={cancelOpen}
        />
      )}

      {cancelOpen && !isClosedByCM && (
        <Card className="border-destructive/30">
          <CardContent className="space-y-3 pt-6">
            <div className="grid gap-3 sm:grid-cols-[1fr_2fr] sm:items-start">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">Reason</Label>
                <Combobox
                  value={cancellationReason}
                  onChange={(v) =>
                    setCancellationReason(v as CreatorRequestCancellationReason)
                  }
                  options={CANCELLATION_REASONS.map((r) => ({
                    value: r,
                    label: cancellationReasonLabel(r),
                  }))}
                  placeholder="Pick a reason"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">
                  Note <span className="text-muted-foreground normal-case">(optional, the campaign manager sees it)</span>
                </Label>
                <Input
                  placeholder="e.g. retry next quarter once budget refreshes"
                  value={cancellationNote}
                  onChange={(e) => setCancellationNote(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setCancelOpen(false);
                  setCancellationReason('');
                  setCancellationNote('');
                }}
              >
                Keep open
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={confirmCancel}
                disabled={update.isPending || !cancellationReason}
              >
                {update.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                Confirm cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Link2 className="h-4 w-4 text-muted-foreground" />
                Attached invites
                <span className="text-xs font-normal text-muted-foreground">
                  {attachedInvites.length}
                </span>
              </CardTitle>
              {!showAttachPanel && !isClosedByCM && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAttachPanel(true)}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Attach
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {attachedInvites.length === 0 ? (
                <EmptyHint
                  icon={<Link2 className="h-4 w-4" />}
                  primary="No invites attached yet"
                  secondary={
                    isClosedByCM
                      ? 'This request is closed, so nothing more can be attached.'
                      : 'Log a reply with this request selected, or attach an invite you already logged.'
                  }
                />
              ) : (
                <ul className="divide-y divide-border/40 overflow-hidden rounded-md border border-border/40">
                  {attachedInvites.map((a) => (
                    <li
                      key={a.attachmentId}
                      className="flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted/40"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2.5">
                        <span className="truncate font-medium text-foreground">
                          @{a.targetIdentifier}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {channelName(a.channel)}
                        </span>
                        <Pill tone={toneFor(a.inviteStatus)}>{inviteStatusLabel(a.inviteStatus)}</Pill>
                        {a.isRoster && (
                          <span className="rounded-sm bg-brand/15 px-1.5 py-0.5 text-[9px] font-mono font-semibold uppercase tracking-wider text-brand">
                            Roster
                          </span>
                        )}
                      </div>
                      {!isClosedByCM && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                          onClick={async () => {
                            if (!request) return;
                            try {
                              await detachInvite.mutateAsync({
                                publicId: request.publicId,
                                inviteId: a.inviteId,
                              });
                              toast.success('Detached');
                            } catch (err) {
                              toast.error((err as Error).message);
                            }
                          }}
                          disabled={detachInvite.isPending}
                          aria-label="Detach"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {showAttachPanel && (
                <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 p-3">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      autoFocus
                      placeholder="Filter by handle (shows your 10 most recent invites)"
                      value={attachSearch}
                      onChange={(e) => setAttachSearch(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  {!attachSearch.trim() && (attachable.data?.data ?? []).length > 0 && (
                    <p className="px-1 text-[10px] uppercase tracking-wider text-muted-foreground/80">
                      Recent invites
                    </p>
                  )}
                  <ul className="max-h-56 space-y-1 overflow-y-auto">
                    {attachable.isLoading ? (
                      <li className="flex items-center gap-2 px-1 py-2 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" /> Loading
                      </li>
                    ) : (attachable.data?.data ?? []).length === 0 ? (
                      <li className="px-1 py-2 text-xs text-muted-foreground">
                        {attachSearch.trim()
                          ? 'No matches in your invites.'
                          : 'No logged invites left to attach.'}
                      </li>
                    ) : (
                      (attachable.data?.data ?? []).map((inv) => (
                        <li
                          key={inv.id}
                          className="flex items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-background"
                        >
                          <div className="flex min-w-0 flex-1 items-center gap-2">
                            <span className="truncate font-medium">
                              @{inv.targetIdentifier}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {channelName(inv.channel)}
                            </span>
                            <InviteStagePill invite={inv} teamTracks={teamTracks} />
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7"
                            onClick={async () => {
                              if (!request) return;
                              try {
                                await attachInvite.mutateAsync({
                                  publicId: request.publicId,
                                  inviteId: inv.id,
                                });
                                toast.success('Attached');
                                setAttachSearch('');
                              } catch (err) {
                                toast.error((err as Error).message);
                              }
                            }}
                            disabled={attachInvite.isPending}
                          >
                            Attach
                          </Button>
                        </li>
                      ))
                    )}
                  </ul>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setShowAttachPanel(false);
                        setAttachSearch('');
                      }}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                Conversation
                <span className="text-xs font-normal text-muted-foreground">
                  with {firstNameOf(request.requesterName) || request.requesterName}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {request.comments.length === 0 ? (
                <EmptyHint
                  icon={<MessageSquare className="h-4 w-4" />}
                  primary="No messages yet"
                  secondary="Leave a note so the campaign manager knows where things stand."
                />
              ) : (
                <ul className="space-y-3">
                  {request.comments.map((c) => {
                    const mine = c.authorSide === 'OUTREACH';
                    const tag = commentAuthorLabel(c);
                    return (
                      <li
                        key={c.id}
                        className={cn(
                          'rounded-md px-3 py-2.5',
                          mine
                            ? 'bg-brand/10 ring-1 ring-brand/15'
                            : 'bg-muted/40',
                        )}
                      >
                        <div className="mb-1 flex items-baseline justify-between gap-3">
                          <div className="flex items-baseline gap-2">
                            <span className="text-sm font-medium">
                              {firstNameOf(c.authorName) || c.authorName}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {tag}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(c.createdAt), 'MMM d · HH:mm')}
                          </span>
                        </div>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                          {c.body}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              )}

              {!isClosedByCM && (
                <div className="space-y-2 border-t border-border/40 pt-4">
                  <textarea
                    className="min-h-[88px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                    placeholder="Reply to the campaign manager…"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        void postComment();
                      }
                    }}
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>⌘+Enter to send</span>
                    <Button
                      size="sm"
                      onClick={postComment}
                      disabled={addComment.isPending || !commentBody.trim()}
                    >
                      {addComment.isPending ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <Send className="mr-1 h-3 w-3" />
                      )}
                      Post
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <aside className="space-y-5">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Brief</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pb-5">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
                <BriefRow label="Type">{REQUEST_TYPE_LABELS[request.requestType]}</BriefRow>
                {request.niches.length > 0 && (
                  <BriefRow label={request.niches.length > 1 ? 'Niches' : 'Niche'}>
                    <div className="flex flex-wrap gap-1.5">
                      {request.niches.map((n) => (
                        <Tag key={n} tone="niche">{nicheName(n)}</Tag>
                      ))}
                    </div>
                  </BriefRow>
                )}
                {request.minFollowers != null && (
                  <BriefRow label="Min followers">
                    {request.minFollowers.toLocaleString()}+
                  </BriefRow>
                )}
                {request.categoryHint && (
                  <BriefRow label="Creator type">{creatorTypeLabel(request.categoryHint)}</BriefRow>
                )}
              </dl>
              {request.brief?.trim() ? (
                <blockquote className="rounded-md bg-muted/40 px-3 py-2.5 text-sm leading-relaxed text-foreground/90">
                  <p className="whitespace-pre-wrap">{request.brief}</p>
                </blockquote>
              ) : (
                <p className="text-xs italic text-muted-foreground">
                  No brief. The campaign manager left it blank.
                </p>
              )}
            </CardContent>
          </Card>

          {isRostered && (
            <Card className="border-success/30 bg-success/5">
              <CardContent className="flex items-start gap-2 pt-5 pb-5 text-sm">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <div>
                  <div className="font-medium text-foreground">Rostered</div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    The campaign manager has been notified. If you clicked this by
                    accident, use the steps above to move it back.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}

/**
 * Per-step tone (drives the pill color). Each funnel step uses its own
 * semantic-color from the existing pill vocabulary so the chain visually
 * progresses from warning (incoming) → info (working) → success (won).
 */
const STEP_TONE: Record<
  (typeof FUNNEL)[number]['value'],
  'warning' | 'info' | 'success'
> = {
  REQUESTED: 'warning',
  IN_PROGRESS: 'info',
  AWAITING_RESPONSE: 'info',
  AWAITING_RATE: 'warning',
  ROSTERED: 'success',
};

/**
 * Pill chain stepper. Each step is a colored pill connected by a thin line.
 * State variants:
 *   - past   : full tone color, opacity 0.85 (we did this)
 *   - current: tone color + ring + bold weight + leading dot
 *   - future : outline only, muted text
 * The connector between two pills picks up the tone of the LEFT pill when
 * that side is past+current, otherwise stays muted.
 */
function StatusFlow({
  current,
  onMove,
  onComplete,
  onCancelStart,
  disabled,
  cancelOpen,
}: {
  current: CreatorRequestStatus;
  onMove: (s: CreatorRequestStatus) => void;
  onComplete: () => void;
  onCancelStart: () => void;
  disabled: boolean;
  cancelOpen: boolean;
}): JSX.Element {
  const currentIndex = FUNNEL.findIndex((s) => s.value === current);
  return (
    <Card>
      <CardContent className="space-y-4 pt-5 pb-5">
        <ol className="flex w-full items-center">
          {FUNNEL.map((step, i) => {
            const state: 'past' | 'current' | 'future' =
              i < currentIndex ? 'past' : i === currentIndex ? 'current' : 'future';
            const tone = STEP_TONE[step.value as keyof typeof STEP_TONE];
            return (
              <li
                key={step.value}
                className={cn(
                  'flex items-center',
                  i < FUNNEL.length - 1 && 'flex-1',
                )}
              >
                <button
                  type="button"
                  disabled={disabled || state === 'current'}
                  onClick={() => onMove(step.value)}
                  aria-current={state === 'current' ? 'step' : undefined}
                  aria-label={`Move to ${step.label}`}
                  className={cn(
                    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-wider transition-all duration-150',
                    state === 'current' && [
                      'font-bold ring-2 ring-offset-2 ring-offset-card',
                      tone === 'warning' && 'bg-warning/25 text-warning ring-warning/40',
                      tone === 'info' && 'bg-info/25 text-info ring-info/40',
                      tone === 'success' && 'bg-success/25 text-success ring-success/40',
                    ],
                    state === 'past' && [
                      'opacity-85 hover:opacity-100',
                      tone === 'warning' && 'bg-warning/15 text-warning',
                      tone === 'info' && 'bg-info/15 text-info',
                      tone === 'success' && 'bg-success/15 text-success',
                    ],
                    state === 'future' && [
                      'border border-border bg-transparent text-muted-foreground hover:border-foreground/40 hover:text-foreground/70',
                    ],
                    state !== 'current' && !disabled && 'cursor-pointer',
                    disabled && 'cursor-wait opacity-50',
                  )}
                >
                  {state === 'current' && (
                    <span
                      aria-hidden
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        tone === 'warning' && 'bg-warning',
                        tone === 'info' && 'bg-info',
                        tone === 'success' && 'bg-success',
                      )}
                    />
                  )}
                  {step.label}
                </button>
                {i < FUNNEL.length - 1 && (
                  <span
                    aria-hidden
                    className={cn(
                      'mx-1.5 h-px flex-1 transition-colors duration-150',
                      i < currentIndex ? 'bg-brand/40' : 'bg-border',
                    )}
                  />
                )}
              </li>
            );
          })}
        </ol>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/40 pt-3">
          <p className="text-xs text-muted-foreground">
            Click any step to move there. Close the request with one of these
            when it's done or stuck.
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onComplete}
              disabled={disabled}
              className="border-success/40 text-success hover:bg-success/10 hover:text-success"
            >
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
              Mark complete
            </Button>
            <Button
              size="sm"
              variant={cancelOpen ? 'destructive' : 'outline'}
              onClick={onCancelStart}
              disabled={disabled}
              className={cn(
                !cancelOpen &&
                  'border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive',
              )}
            >
              <XCircle className="mr-1.5 h-3.5 w-3.5" />
              {cancelOpen ? 'Pick a reason below' : 'Cancel request'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BriefRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <>
      <dt className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground self-center">
        {label}
      </dt>
      <dd className="text-sm font-medium text-foreground">{children}</dd>
    </>
  );
}

function EmptyHint({
  icon,
  primary,
  secondary,
}: {
  icon: React.ReactNode;
  primary: string;
  secondary: string;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3 rounded-md border border-dashed border-border/60 bg-muted/20 px-3 py-3">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-foreground">{primary}</p>
        <p className="text-xs text-muted-foreground">{secondary}</p>
      </div>
    </div>
  );
}

function ClosedBanner({
  tone,
  title,
  body,
}: {
  tone: 'success' | 'destructive';
  title: string;
  body: string | null;
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border px-4 py-3',
        tone === 'success'
          ? 'border-success/30 bg-success/5'
          : 'border-destructive/30 bg-destructive/5',
      )}
    >
      <ChevronRight
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          tone === 'success' ? 'text-success' : 'text-destructive',
        )}
      />
      <div className="space-y-0.5">
        <p
          className={cn(
            'text-sm font-medium',
            tone === 'success' ? 'text-success' : 'text-destructive',
          )}
        >
          {title}
        </p>
        {body && <p className="text-sm text-foreground/80">{body}</p>}
      </div>
    </div>
  );
}
